import { Chip } from "@mui/material";
import _ from "lodash";
import React, { useMemo } from "react";
import { Column, Filters, SortingRule } from "react-table";
import { SelectOption } from "../../../common/components/input/Select";
import { SelectFilter } from "../../../common/components/table/filters/SelectFilter";
import {
  PaginatedTable,
  PaginatedTableProps,
} from "../../../common/components/table/PaginatedTable";
import { DataSubjectRequestSummaryDto } from "../../../common/service/server/dto/DataSubjectRequestDto";
import type { CollectionGroupReferenceDto } from "../../../common/service/server/dto/CollectionGroupDto";
import { PaginatedMetaDto } from "../../../common/service/server/dto/PaginatedDto";
import { DataRequestFilter } from "../../../common/service/server/Types";
import { SimplifiedRegionRequestTypeDto } from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { Fmt } from "../../formatters";
import { WAITING_FOR_CONSUMER_STATUS, fmtRequestTypeState } from "../../formatters/request";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../hooks";
import { OVERRIDEABLE_REGIONS } from "../../components/superorg/settings/ConsentBannerSettings";
import { makeColumns } from "../../hooks/makeColumns";
import { RouteHelpers } from "../../root/routes";
import useStatefulPagination from "../../../common/hooks/useStatefulPagination";
import { UseRowSelectionManager } from "../../../common/hooks/useRowSelectionManager";
import clsx from "clsx";
import { Pill, SimpleCheckboxControlled } from "@truevault/aquilo";
import { usePrivacyCenter } from "../../hooks/usePrivacyCenter";
import { shouldIncludeOtherLocationOption } from "../../utils/locationUtils";

const formatDate = (date?: string): string => {
  if (date === undefined || date === null || date === "") {
    return "";
  }

  return new Date(date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const relevantDate = (tab: string, row: DataSubjectRequestSummaryDto): string => {
  switch (tab.toLowerCase()) {
    case "active":
      return row.dueDate;
    case "complete":
      return row.closedAt;
    case "pending":
      return row.requestDate;
    default:
      return "";
  }
};

const dueDateId = (tab: string): string => {
  switch (tab.toLowerCase()) {
    case "active":
      return "dueIn";
    case "complete":
      return "closedAt";
    case "pending":
      return "submitted";
    default:
      return "dueIn";
  }
};

const getDueDateColumn = (tab: string) => {
  let dueDateLabel = "Due Date";

  switch (tab.toLowerCase()) {
    case "complete":
      dueDateLabel = "Closed";
      break;
    case "pending":
      dueDateLabel = "Submitted";
      break;
  }

  return dueDateLabel;
};

const getCrunchLabelConfig = (
  consumerGroups?: CollectionGroupReferenceDto[] | null,
): { label: string; color: "primary" | "secondary" | "success" } | null => {
  if (!consumerGroups?.length) {
    return null;
  }

  for (const group of consumerGroups) {
    const groupName = group?.name;

    if (groupName === "Franchise Consumer") {
      return { label: "Franchise", color: "success" };
    }
    if (groupName === "Corporate Consumer") {
      return { label: "Corporate", color: "secondary" };
    }
  }

  return null;
};

export const getRequestTypeFilterOptions = (isGdprOrg: boolean) => {
  let optionsArray = [...US_TYPE_FILTER_OPTIONS];
  if (isGdprOrg) {
    optionsArray = [...optionsArray, ...GDPR_TYPE_FILTER_OPTIONS];
  }

  return _.sortBy(_.uniqBy(optionsArray, "value"), "value");
};

export const US_TYPE_FILTER_OPTIONS: SelectOption[] = [
  {
    value: "access",
    label: "Access",
  },
  {
    value: "appeal",
    label: "Appeal",
  },
  {
    value: "correction",
    label: "Correction",
  },
  {
    value: "delete",
    label: "Delete",
  },
  {
    value: "limit",
    label: "Limit",
  },
  {
    value: "optout",
    label: "Opt-Out",
  },
  {
    value: "profilingOptOut",
    label: "Profiling Opt-Out",
  },
];

export const GDPR_TYPE_FILTER_OPTIONS: SelectOption[] = [
  {
    value: "access",
    label: "Access",
  },
  {
    value: "delete",
    label: "Delete",
  },
  {
    value: "objection",
    label: "Objection to Processing",
  },
  {
    value: "correction",
    label: "Rectification",
  },
  {
    value: "withdrawal",
    label: "Withdrawal of Consent",
  },
  {
    value: "portability",
    label: "Portability",
  },
  {
    value: "restrictProcessing",
    label: "Restrict Processing",
  },
];

const DATE_FILTER_OPTIONS: SelectOption[] = [
  {
    value: -1,
    label: "Past Due",
  },
  {
    value: 1,
    label: "Next Day",
  },
  {
    value: 7,
    label: "Next 7 Days",
  },
  {
    value: 30,
    label: "Next 30 Days",
  },
];

export const STATE_FILTER_OPTIONS: SelectOption[] = [
  {
    value: "pending",
    label: "Pending",
  },
  {
    value: "closed",
    label: "Closed",
  },
  {
    value: "new",
    label: "New",
  },
  {
    value: "in_progress",
    label: "In Progress",
  },
];

const STATIC_US_COUNTRY_REGION_OPTIONS: SelectOption[] = OVERRIDEABLE_REGIONS.slice()
  .sort((a, b) => a.label.localeCompare(b.label))
  .map((r) => ({ value: r.region, label: `US - ${r.label}` }));

const buildUsCountryRegionOptionsFromMap = (
  regionRequestTypeMap?: Record<string, SimplifiedRegionRequestTypeDto> | null,
): SelectOption[] => {
  if (!regionRequestTypeMap) {
    return STATIC_US_COUNTRY_REGION_OPTIONS;
  }

  const options = new Map<string, SelectOption>();

  Object.entries(regionRequestTypeMap).forEach(([regionCode, mapping]) => {
    if (!regionCode.startsWith("US-")) return;
    const regionGroup = (mapping.regionGroup || "").toUpperCase();
    const isUnitedStates = regionGroup === "UNITED_STATES";
    if (!isUnitedStates) {
      return;
    }

    options.set(regionCode, {
      value: regionCode,
      label: `US - ${mapping.regionName}`,
    });
  });

  return Array.from(options.values()).sort((a, b) =>
    String(a.label).localeCompare(String(b.label)),
  );
};

export const getCountryRegionFilterOptions = (
  org: {
    featureGdpr?: boolean;
    featurePipeda?: boolean;
  } | null,
  regionRequestTypeMap?: Record<string, SimplifiedRegionRequestTypeDto> | null,
  includeOtherOption: boolean = true,
): SelectOption[] => {
  const usCountryRegionOptions = buildUsCountryRegionOptionsFromMap(regionRequestTypeMap);

  const baseOptions: SelectOption[] = includeOtherOption
    ? [{ value: "OTHER", label: "Other" }, ...usCountryRegionOptions]
    : usCountryRegionOptions.filter(
        (option) =>
          !option.value.toUpperCase().includes("OTHER") &&
          !String(option.label).toLowerCase().includes("non-privacy states"),
      );

  if (!org) return baseOptions;
  const options: SelectOption[] = [];
  if (org.featurePipeda) options.push({ value: "CANADA", label: "Canada" });
  if (org.featureGdpr) options.push({ value: "EEA_UK", label: "EEA/UK" });
  return [...options, ...baseOptions];
};

const useColumns = (
  includeName: boolean = false,
  includeSelection: boolean = false,
  rowSelectionManager: UseRowSelectionManager<any>,
) =>
  makeColumns((tab: string): Column<any>[] => {
    return [
      includeSelection && {
        Header: (
          <div className="tw-boundary">
            {rowSelectionManager?.hasVisibleRows ? (
              <SimpleCheckboxControlled
                name="request-table-select-all"
                checked={rowSelectionManager?.areAllVisibleRowsSelected()}
                onChange={(newChecked) => {
                  rowSelectionManager?.setSelectionForVisible(newChecked);
                }}
              />
            ) : null}
          </div>
        ),
        id: "select",
        disableSortBy: true,
        disableFilters: true,
        accessor: (row) => row as DataSubjectRequestSummaryDto,
        Cell: ({ value }) =>
          !value.batchPublicId && (
            <SelectionCell selectionManager={rowSelectionManager} rowData={value} />
          ),
      },
      {
        Header: "Status",
        id: "state",
        accessor: "state",
        Filter: (props) => (
          <SelectFilter
            {...props}
            options={STATE_FILTER_OPTIONS}
            placeholder="Request Status"
            listboxMaxHeight={300}
          />
        ),
        Cell: ({ row, value }) =>
          row.original?.batchPublicId && tab === "active" ? (
            <div className="tw-boundary">
              <Pill variant="info">Batched</Pill>
            </div>
          ) : (
            <div className="tw-boundary">
              <div className="flex items-center gap-3">
                <RequestIconCell row={row} />
                <div className="text-neutral-900 text-[18px] font-medium">
                  {fmtRequestTypeState(value)}
                </div>
              </div>
            </div>
          ),
        filterDisplayOrder: 2,
      },
      {
        Header: "Requester Email",
        id: "subjectEmailAddress",
        accessor: "subjectEmailAddress",
        disableFilters: true,
        Cell: ({ value, row }) => (
          <b
            className={clsx(
              row.original?.batchPublicId && tab === "active"
                ? ["text-disabled", "text-style-italic"]
                : null,
            )}
          >
            {value || Fmt.DataSubject.fmtFullName(row.original as DataSubjectRequestSummaryDto)}
          </b>
        ),
      },
      includeName && {
        Header: "First Name",
        id: "subjectFirstName",
        accessor: "subjectFirstName",
      },
      includeName && {
        Header: "Last Name",
        id: "subjectLastName",
        accessor: "subjectLastName",
      },
      {
        Header: "Request Type",
        id: "type",
        accessor: (row) => {
          return row.dataRequestType.shortLabel;
        },
        Cell: ({ value, row }) => {
          const crunchLabelConfig = getCrunchLabelConfig(row.original.consumerGroups);

          return (
            <>
              {value || "Unknown"}
              {row.original.context === "EMPLOYEE" ? (
                <Chip className="ml-md" label="HR" color="secondary" size="small" />
              ) : undefined}
              {crunchLabelConfig ? (
                <Chip
                  className="ml-md"
                  label={crunchLabelConfig.label}
                  color={crunchLabelConfig.color}
                  size="small"
                />
              ) : undefined}
            </>
          );
        },
        Filter: (props) => {
          const [org, orgRequest] = usePrimaryOrganization();

          const options = useMemo(() => {
            if (!org) {
              return [];
            }
            return getRequestTypeFilterOptions(org.featureGdpr);
          }, [org]);

          return (
            <SelectFilter
              {...props}
              loading={orgRequest.running}
              options={options}
              placeholder="Request Type"
              listboxMaxHeight={300}
            />
          );
        },
        filterDisplayOrder: 1,
      },
      {
        Header: getDueDateColumn(tab),
        id: dueDateId(tab),
        disableFilters: tab !== "active",
        accessor: (row) => relevantDate(tab, row as DataSubjectRequestSummaryDto),
        Cell: ({ value }) => <>{formatDate(value)}</>,
        Filter: (props) => (
          <SelectFilter
            {...props}
            options={DATE_FILTER_OPTIONS}
            placeholder="Due Date"
            listboxMaxHeight={300}
          />
        ),
        filterDisplayOrder: 3,
      },
      {
        Header: "Country & Region",
        id: "region",
        disableSortBy: true,
        Filter: (props) => {
          const orgId = usePrimaryOrganizationId();
          const [org, orgRequest] = usePrimaryOrganization();
          const [privacyCenter, privacyCenterRequest] = usePrivacyCenter(orgId);
          const includeOtherOption = shouldIncludeOtherLocationOption(privacyCenter);
          const options = useMemo(
            () =>
              getCountryRegionFilterOptions(
                org ?? null,
                privacyCenter?.regionRequestTypeMap,
                includeOtherOption,
              ),
            [includeOtherOption, org, privacyCenter?.regionRequestTypeMap],
          );
          return (
            <SelectFilter
              {...props}
              loading={orgRequest.running || privacyCenterRequest.running}
              options={options}
              placeholder="Country & Region"
              listboxMaxHeight={300}
            />
          );
        },
        accessor: (row) => {
          const region = (row as DataSubjectRequestSummaryDto).dataRequestType?.regulationLaw
            ?.region;
          if (!region) return "";
          if (region.regionGroup === "EEA_UK") return "EEA/UK";
          if (region.regionGroup === "CANADA") return "Canada";
          if (region.regionGroup === "UNITED_STATES") return `US - ${region.regionName}`;
          if (region.regionGroup === "OTHER") return "Other";
          return region.regionName || "";
        },
        Cell: ({ value }) => <>{value || "-"}</>,
        filterDisplayOrder: 4,
      },
    ].filter((column) => column);
  });

function RequestIconCell({ row }: { row: any }) {
  const status = fmtRequestTypeState(row.original.state);

  if (status === "New") {
    return (
      <svg
        width="16"
        height="14"
        viewBox="0 0 16 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.0117 4.01172V2.5L8 6.26172L1.98828 2.5V4.01172L8 7.73828L14.0117 4.01172ZM14.0117 0.988281C14.4102 0.988281 14.75 1.14062 15.0312 1.44531C15.3359 1.75 15.4883 2.10156 15.4883 2.5V11.5C15.4883 11.8984 15.3359 12.25 15.0312 12.5547C14.75 12.8594 14.4102 13.0117 14.0117 13.0117H1.98828C1.58984 13.0117 1.23828 12.8594 0.933594 12.5547C0.652344 12.25 0.511719 11.8984 0.511719 11.5V2.5C0.511719 2.10156 0.652344 1.75 0.933594 1.44531C1.23828 1.14062 1.58984 0.988281 1.98828 0.988281H14.0117Z"
          fill="#D1CED9"
        />
      </svg>
    );
  } else if (status === "Closed") {
    return (
      <svg
        width="14"
        height="11"
        viewBox="0 0 14 11"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4.75 8.12891L12.6953 0.183594L13.75 1.23828L4.75 10.2383L0.566406 6.05469L1.62109 5L4.75 8.12891Z"
          fill="#D1CED9"
        />
      </svg>
    );
  } else if (status === WAITING_FOR_CONSUMER_STATUS) {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask
          id="mask0_1512_17119"
          style={{ maskType: "alpha" }}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="20"
          height="20"
        >
          <rect width="20" height="20" fill="#D9D9D9" />
        </mask>
        <g mask="url(#mask0_1512_17119)">
          <path
            d="M9.99992 19.1663C8.44436 19.1663 7.01381 18.8122 5.70825 18.1038C4.4027 17.3955 3.33325 16.4511 2.49992 15.2705V17.4997H0.833252V12.4997H5.83325V14.1663H3.77075C4.43742 15.1663 5.31589 15.9719 6.40617 16.583C7.49645 17.1941 8.69436 17.4997 9.99992 17.4997C11.0416 17.4997 12.0173 17.3018 12.927 16.9059C13.8367 16.5101 14.6284 15.9754 15.302 15.3018C15.9756 14.6281 16.5103 13.8365 16.9062 12.9268C17.302 12.017 17.4999 11.0413 17.4999 9.99967H19.1666C19.1666 11.2636 18.927 12.4511 18.4478 13.5622C17.9687 14.6733 17.3124 15.6455 16.4791 16.4788C15.6458 17.3122 14.6735 17.9684 13.5624 18.4476C12.4513 18.9268 11.2638 19.1663 9.99992 19.1663ZM0.833252 9.99967C0.833252 8.73579 1.07284 7.54829 1.552 6.43717C2.03117 5.32606 2.68742 4.35384 3.52075 3.52051C4.35409 2.68717 5.32631 2.03092 6.43742 1.55176C7.54853 1.07259 8.73603 0.833008 9.99992 0.833008C11.5555 0.833008 12.986 1.18717 14.2916 1.89551C15.5971 2.60384 16.6666 3.54829 17.4999 4.72884V2.49967H19.1666V7.49967H14.1666V5.83301H16.2291C15.5624 4.83301 14.6839 4.02745 13.5937 3.41634C12.5034 2.80523 11.3055 2.49967 9.99992 2.49967C8.95825 2.49967 7.98256 2.69759 7.07284 3.09342C6.16311 3.48926 5.37145 4.02398 4.69784 4.69759C4.02422 5.3712 3.4895 6.16287 3.09367 7.07259C2.69784 7.98231 2.49992 8.95801 2.49992 9.99967H0.833252Z"
            fill="#D1CED9"
          />
        </g>
      </svg>
    );
  } else if (status === "Pending Automation") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.63452 1.49684C9.82279 1.60181 9.91521 1.82164 9.85849 2.0296L8.61372 6.59377H13.1563C13.3429 6.59377 13.5118 6.70451 13.5862 6.8757C13.6606 7.04689 13.6263 7.2459 13.4989 7.38235L6.93644 14.4136C6.78936 14.5712 6.55378 14.6082 6.3655 14.5032C6.17723 14.3982 6.08481 14.1784 6.14153 13.9704L7.3863 9.40627H2.84376C2.65711 9.40627 2.48824 9.29553 2.41385 9.12434C2.33946 8.95315 2.37372 8.75413 2.50108 8.61768L9.06358 1.58643C9.21066 1.42885 9.44624 1.39188 9.63452 1.49684Z"
          fill="#D1CED9"
        />
      </svg>
    );
  } else if (status === "In Progress") {
    return (
      <svg
        width="14"
        height="18"
        viewBox="0 0 14 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7 13.5V11.25L9.98828 14.2383L7 17.2617V15.0117C5.35938 15.0117 3.94141 14.4258 2.74609 13.2539C1.57422 12.0586 0.988281 10.6406 0.988281 9C0.988281 7.82812 1.30469 6.76172 1.9375 5.80078L3.02734 6.89062C2.67578 7.52344 2.5 8.22656 2.5 9C2.5 10.2422 2.93359 11.3086 3.80078 12.1992C4.69141 13.0664 5.75781 13.5 7 13.5ZM7 2.98828C8.64062 2.98828 10.0469 3.58594 11.2188 4.78125C12.4141 5.95312 13.0117 7.35938 13.0117 9C13.0117 10.1719 12.6953 11.2383 12.0625 12.1992L10.9727 11.1094C11.3242 10.4766 11.5 9.77344 11.5 9C11.5 7.75781 11.0547 6.70312 10.1641 5.83594C9.29688 4.94531 8.24219 4.5 7 4.5V6.75L4.01172 3.76172L7 0.738281V2.98828Z"
          fill="#D1CED9"
        />
      </svg>
    );
  }

  return null;
}

function SelectionCell({
  selectionManager,
  rowData,
}: {
  selectionManager: UseRowSelectionManager<DataSubjectRequestSummaryDto>;
  rowData: DataSubjectRequestSummaryDto;
}) {
  return (
    <div className="tw-boundary">
      <SimpleCheckboxControlled
        name={`request-table-row-select-${rowData.id}`}
        checked={selectionManager.isRowSelected(rowData.id)}
        onChange={(newChecked) => {
          selectionManager.onRowSelectChange(rowData.id, newChecked, rowData);
        }}
      />
    </div>
  );
}

type RequestsTableProps = {
  columnSet: DataRequestFilter;
  data: DataSubjectRequestSummaryDto[];
  allDataForCurrentFilter?: DataSubjectRequestSummaryDto[];
  loading: boolean;
  pageMeta: PaginatedMetaDto;
  showFilters?: boolean;
  includeName?: boolean;
  onlyShowHrRequests?: boolean;
  onPageChange: (pageIndex: number) => void | Promise<void>;
  onSortChange: (sortBy: SortingRule<any>[]) => void | Promise<void>;
  onFiltersChange?: (sortBy: Filters<any>) => void | Promise<void>;
  onGlobalSearchChange?: (searchTerm: string) => void | Promise<void>;
  initialPageIndex: number;
  initialSortBy: SortingRule<any>[];
  initialFilters?: Filters<any>;
  emptyState?: React.ReactNode;
  allowRowSelection?: boolean;
  rowSelectionManager?: UseRowSelectionManager<DataSubjectRequestSummaryDto>;
  reloadDataHandler?: () => void;
  batchesTable?: React.ReactNode;
} & Partial<PaginatedTableProps<DataSubjectRequestSummaryDto>>;

const isOtherRegion = (row: DataSubjectRequestSummaryDto) =>
  row.dataRequestType?.regulationLaw?.region?.regionGroup === "OTHER";

const getRowRegionCode = (row: DataSubjectRequestSummaryDto): string | null => {
  const regionCode = row.dataRequestType?.regulationLaw?.region?.regionCode;
  if (!regionCode) return null;
  const normalized = regionCode.trim().toUpperCase();
  return normalized.length ? normalized : null;
};

const getRowRegionGroup = (row: DataSubjectRequestSummaryDto): string | null => {
  const regionGroup = row.dataRequestType?.regulationLaw?.region?.regionGroup;
  if (!regionGroup) return null;
  const normalized = regionGroup.trim().toUpperCase();
  return normalized.length ? normalized : null;
};

export const matchesCountryRegionFilter = (
  row: DataSubjectRequestSummaryDto,
  regionFilterValue?: string | null,
): boolean => {
  const normalizedFilter = regionFilterValue?.trim().toUpperCase();
  if (!normalizedFilter) return true;

  const rowRegionCode = getRowRegionCode(row);
  const rowRegionGroup = getRowRegionGroup(row);

  if (normalizedFilter.startsWith("US-")) {
    return rowRegionCode === normalizedFilter;
  }

  if (normalizedFilter === "OTHER") {
    return rowRegionGroup === "OTHER";
  }

  if (normalizedFilter === "EEA_UK") {
    return rowRegionGroup === "EEA_UK";
  }

  if (normalizedFilter === "CANADA") {
    return rowRegionGroup === "CANADA";
  }

  return rowRegionCode === normalizedFilter || rowRegionGroup === normalizedFilter;
};

export function RequestsTable({
  columnSet,
  data,
  allDataForCurrentFilter,
  includeName,
  allowRowSelection,
  rowSelectionManager,
  onlyShowHrRequests,
  initialFilters,
  ...rest
}: RequestsTableProps) {
  const regionFilterValue = initialFilters?.find((f) => f.id === "region")?.value as
    | string
    | undefined;
  const excludeOtherRows = regionFilterValue && regionFilterValue !== "OTHER";

  const filteredData = useMemo(() => {
    let result = data ?? [];
    if (onlyShowHrRequests) result = result.filter((row) => row.context === "EMPLOYEE");
    if (regionFilterValue) {
      result = result.filter((row) => matchesCountryRegionFilter(row, regionFilterValue));
    }
    if (excludeOtherRows) result = result.filter((row) => !isOtherRegion(row));
    return result;
  }, [data, onlyShowHrRequests, regionFilterValue, excludeOtherRows]);

  const filteredAllData = useMemo(() => {
    let result = allDataForCurrentFilter ?? [];
    if (onlyShowHrRequests) result = result.filter((row) => row.context === "EMPLOYEE");
    if (regionFilterValue) {
      result = result.filter((row) => matchesCountryRegionFilter(row, regionFilterValue));
    }
    if (excludeOtherRows) result = result.filter((row) => !isOtherRegion(row));
    return result;
  }, [allDataForCurrentFilter, onlyShowHrRequests, regionFilterValue, excludeOtherRows]);

  const columns = useColumns(
    includeName,
    allowRowSelection && columnSet === "active" && !!rowSelectionManager,
    rowSelectionManager,
  )(columnSet, [rowSelectionManager]);

  const { linkTo } = useStatefulPagination();
  const canClick = (row: any) =>
    columnSet !== "pending" && (!row.original?.batchPublicId || columnSet === "complete"); // Do not allow interacting w/ batched requests unless they're closed

  return (
    <PaginatedTable
      enableRowClick={(row) => canClick(row)}
      onRowClick={(row) => linkTo(RouteHelpers.requests.detail(row.original.id))}
      columns={columns}
      rowSelectionManager={rowSelectionManager}
      data={filteredData}
      allDataForCurrentFilter={filteredAllData}
      initialFilters={initialFilters}
      {...rest}
    />
  );
}
